import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import httpService from "../services/httpService";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { FaArrowLeft, FaCheckCircle, FaInfoCircle } from "react-icons/fa";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import LoadingFade from "../components/LoadingFade";
import ClubDetailsModal from "../components/ListenerComponents/ClubDetailsModal";
import { getImage } from "../utils/podcastClubUtils";
import "../styles/dashbaords.scss";

const ListenerPodcasterClubs = () => {
    const { guid } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [clubs, setClubs] = useState([]);
    const [isFollowed, setIsFollowed] = useState(location.state?.is_followed ?? null);
    const [followLoading, setFollowLoading] = useState(false);
    const [showClubDetailsModal, setShowClubDetailsModal] = useState(false);
    const [selectedClubGuid, setSelectedClubGuid] = useState(null);

    const handleFollowToggle = () => {
        setFollowLoading(true);
        httpService.post(`/dash/podcasters/${guid}/follow`)
            .then((res) => {
                setIsFollowed(res.data.followed);
            })
            .catch((err) => {
                setError(err.response?.data?.message || "Error updating follow state");
            })
            .finally(() => setFollowLoading(false));
    };

    useEffect(() => {
        httpService.get(`/dash/podcasters/${guid}/clubs`)
            .then((res) => {
                setClubs(res.data.data);
            })
            .catch((err) => {
                setError(err.response?.data?.message || "Error loading clubs");
            })
            .finally(() => setLoading(false));
    }, [guid]);

    const handleReadMore = (clubGuid) => {
        setSelectedClubGuid(clubGuid);
        setShowClubDetailsModal(true);
    };

    const handleCloseModal = () => {
        setShowClubDetailsModal(false);
        setSelectedClubGuid(null);
    };

    return (
        <div id="ListenerPodcasterClubs-component" className="container">
            <div className="my-4">
                <Button variant="link" className="p-0 mb-3" onClick={() => navigate(-1)}>
                    <FaArrowLeft className="me-1" /> Back
                </Button>

                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="mb-0">Podcaster's Clubs</h2>
                    <Button
                        variant={isFollowed ? "outline-primary" : "primary"}
                        onClick={handleFollowToggle}
                        disabled={followLoading}
                    >
                        {followLoading ? "..." : isFollowed ? "Unfollow" : "Follow"}
                    </Button>
                </div>

                {error && <div className="alert alert-danger">{error}</div>}

                <LoadingFade loading={loading} stateKey={loading ? 'loading' : 'not-loading'}>
                    {clubs.length > 0 ? (
                        <Row>
                            {clubs.map((club) => (
                                <Col key={club.id} md={6} className="mb-4">
                                    <Card className="h-100 p-0 overflow-hidden shadow-sm rounded-2">
                                        <Card.Body className="d-flex flex-column p-0">
                                            <Row className="flex-grow-1">
                                                <Col sm={2} className="p-0">
                                                    <div
                                                        className="club-image-bg"
                                                        style={{ backgroundImage: `url(${getImage(club, 'tall')})` }}
                                                        role="img"
                                                        aria-label={club.title}
                                                    />
                                                </Col>
                                                <Col sm={10} className="p-3 d-flex flex-column justify-content-between">
                                                    <div className="mb-3 flex-grow-1">
                                                        <Card.Title>{club.title}</Card.Title>
                                                        <p>{club.description}</p>
                                                        <small className="text-muted d-block mb-2">Skills You Can Practice:</small>
                                                        {club.skills && club.skills.length > 0 ? (
                                                            <div>
                                                                {club.skills.map((skill) => (
                                                                    <Badge key={skill.id} bg="info" className="me-1 mb-1">
                                                                        {skill.title}
                                                                    </Badge>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted">No skills listed</span>
                                                        )}
                                                    </div>

                                                    <div className="d-flex justify-content-between align-items-center mt-auto px-3">
                                                        <div className="d-flex align-items-center">
                                                            <FaCheckCircle
                                                                size={20}
                                                                className={'icon me-2 ' + (club.is_subscribed ? 'subscribed' : 'not-subscribed')}
                                                            />
                                                            <small className="text-muted">
                                                                {club.is_subscribed ? 'Subscribed' : 'Not subscribed'}
                                                            </small>
                                                        </div>
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Read more</Tooltip>}>
                                                            <Button
                                                                variant="primary"
                                                                size="md"
                                                                onClick={() => handleReadMore(club.guid)}
                                                            >
                                                                <FaInfoCircle />
                                                            </Button>
                                                        </OverlayTrigger>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    ) : (
                        !loading && (
                            <div className="text-center my-4">
                                <p className="text-muted">This podcaster has no public clubs yet.</p>
                            </div>
                        )
                    )}
                </LoadingFade>
            </div>

            <ClubDetailsModal
                show={showClubDetailsModal}
                onHide={handleCloseModal}
                clubGuid={selectedClubGuid}
            />
        </div>
    );
};

export default ListenerPodcasterClubs;
